import type { LimbId } from "../combat/health.js";
import { raycastAABB } from "../math/aabb.js";
import type { Vec3 } from "../math/vec3.js";
import type { Stance } from "../protocol/messages.js";

/**
 * Analytic limb hitboxes from a network pose. Derived from `(pos, yaw, stance,
 * lean)` — already on the wire — rather than a skeleton, so server and client
 * compute identical boxes, and rewinding an entity stores four numbers per
 * tick.
 */

/**
 * The minimum an entity must publish to be shootable. `pos` is the FEET,
 * matching `EntitySnap.pos`. Pitch is absent: the rig's torso does not bend
 * with the view.
 */
export interface HitPose {
  pos: Vec3;
  yaw: number;
  stance: Stance;
  /**
   * Peek direction: −1 left, +1 right, 0 or absent upright. Folded in, unlike
   * pitch, because it moves the body a quarter of a metre sideways.
   */
  lean?: -1 | 0 | 1;
}

/**
 * One limb's box in ENTITY-LOCAL space: origin at the feet, +Y up, facing −Z.
 */
export interface LimbBox {
  limb: LimbId;
  min: Vec3;
  max: Vec3;
}

/**
 * Standing silhouette, metres, entity-local, inside the 1.8 m × 0.35 collision
 * capsule (head crown at 1.80, arms out to ±0.34) — you can only be shot where
 * you could stand. The one exception is a LEAN: head and shoulder slide past
 * the capsule on purpose, because exposure is the price of the peek, and the
 * rig leans by the same amounts (`render/character.ts`).
 *
 * Legs 0 → 0.86 (`legL` on the negative x side to match the rig), stomach
 * 0.86 → 1.12, thorax 1.12 → 1.48, head 1.48 → 1.80; arms 0.88 → 1.40 overlap
 * the thorax slightly, and the nearest-hit rule in `raycastPose` resolves that.
 */
const STAND_BOXES: readonly LimbBox[] = freezeBoxes([
  box("head", -0.13, 1.48, -0.13, 0.13, 1.8, 0.13),
  box("thorax", -0.23, 1.12, -0.13, 0.23, 1.48, 0.13),
  box("stomach", -0.19, 0.86, -0.13, 0.19, 1.12, 0.13),
  box("armL", -0.34, 0.88, -0.13, -0.21, 1.4, 0.13),
  box("armR", 0.21, 0.88, -0.13, 0.34, 1.4, 0.13),
  box("legL", -0.19, 0, -0.13, 0, 0.86, 0.13),
  box("legR", 0, 0, -0.13, 0.19, 0.86, 0.13),
]);

/**
 * Crouched silhouette: the standing boxes with y scaled by 1.3 / 1.8. Not what
 * the rig does (it folds the legs), but a single multiply, and it errs in the
 * DEFENDER's favour on the head.
 */
const CROUCH_SCALE_Y = 1.3 / 1.8;

const CROUCH_BOXES: readonly LimbBox[] = freezeBoxes(
  STAND_BOXES.map((b) =>
    box(
      b.limb,
      b.min.x,
      b.min.y * CROUCH_SCALE_Y,
      b.min.z,
      b.max.x,
      b.max.y * CROUCH_SCALE_Y,
      b.max.z,
    ),
  ),
);

/**
 * How far each limb slides sideways at full lean, metres, +x for a right lean.
 * A pure lateral TRANSLATION, not a rotation, so every box stays an AABB and
 * the hit path stays cheap. The head travels furthest, the feet not at all, and
 * BOTH arms move — the body translates, it does not squeeze. Same for crouched.
 */
const LEAN_SHIFT_X: Readonly<Record<LimbId, number>> = {
  head: 0.26,
  thorax: 0.16,
  stomach: 0.07,
  armL: 0.22,
  armR: 0.22,
  legL: 0,
  legR: 0,
};

/** One stance's boxes, slid sideways for a lean. Module load only. */
function leanBoxes(base: readonly LimbBox[], dir: -1 | 1): readonly LimbBox[] {
  return freezeBoxes(
    base.map((b) => {
      const dx = LEAN_SHIFT_X[b.limb] * dir;
      return box(b.limb, b.min.x + dx, b.min.y, b.min.z, b.max.x + dx, b.max.y, b.max.z);
    }),
  );
}

/**
 * The six box sets (two stances × three leans), built once at module load so
 * selecting a pose's boxes is two comparisons and no allocation.
 */
const STAND_LEAN_L = leanBoxes(STAND_BOXES, -1);
const STAND_LEAN_R = leanBoxes(STAND_BOXES, 1);
const CROUCH_LEAN_L = leanBoxes(CROUCH_BOXES, -1);
const CROUCH_LEAN_R = leanBoxes(CROUCH_BOXES, 1);

function box(
  limb: LimbId,
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): LimbBox {
  return {
    limb,
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ },
  };
}

/** Frozen so a caller handing these to a renderer cannot scribble on them. */
function freezeBoxes(boxes: LimbBox[]): readonly LimbBox[] {
  for (const b of boxes) {
    Object.freeze(b.min);
    Object.freeze(b.max);
    Object.freeze(b);
  }
  return Object.freeze(boxes);
}

/**
 * The precomputed box set for a stance and lean. Never allocates. An upright
 * call returns the very same array object it did before leaning existed —
 * several callers and one test rely on the identity.
 */
export function limbBoxesFor(stance: Stance, lean: -1 | 0 | 1 = 0): readonly LimbBox[] {
  const crouched = stance === "crouch";
  if (lean === 0) return crouched ? CROUCH_BOXES : STAND_BOXES;
  if (lean < 0) return crouched ? CROUCH_LEAN_L : STAND_LEAN_L;
  return crouched ? CROUCH_LEAN_R : STAND_LEAN_R;
}

export interface LimbRayHit {
  limb: LimbId;
  /** Distance along the ray, in units of `dir` (metres for a unit `dir`). */
  dist: number;
}

// Module-level scratch (see collision.ts): called per rewound entity per shot.
const localOrigin: Vec3 = { x: 0, y: 0, z: 0 };
const localDir: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * Casts a ray against one posed entity and returns the NEAREST limb hit. The
 * ray goes into entity-local space (one rotation of two vectors; the boxes
 * stay AABBs); yaw 0 faces −Z and local→world is `(x·cos + z·sin, y, −x·sin +
 * z·cos)`. `dir` should be unit. `boxes` defaults to the human silhouette; an
 * animal passes `SPECIES[...].boxes`.
 */
export function raycastPose(
  origin: Vec3,
  dir: Vec3,
  maxDist: number,
  pose: HitPose,
  // `?? 0` and not a required field: a pose built before leaning existed, or by
  // a caller with no opinion on it, is an upright pose — see `HitPose.lean`.
  boxes: readonly LimbBox[] = limbBoxesFor(pose.stance, pose.lean ?? 0),
): LimbRayHit | null {
  if (maxDist <= 0) return null;
  if (dir.x === 0 && dir.y === 0 && dir.z === 0) return null;

  const c = Math.cos(pose.yaw);
  const s = Math.sin(pose.yaw);

  const dx = origin.x - pose.pos.x;
  const dy = origin.y - pose.pos.y;
  const dz = origin.z - pose.pos.z;

  localOrigin.x = dx * c - dz * s;
  localOrigin.y = dy;
  localOrigin.z = dx * s + dz * c;

  localDir.x = dir.x * c - dir.z * s;
  localDir.y = dir.y;
  localDir.z = dir.x * s + dir.z * c;

  let bestDist = Infinity;
  let bestLimb: LimbId | null = null;

  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i] as LimbBox;
    const t = raycastAABB(localOrigin, localDir, maxDist, b);
    if (t !== null && t < bestDist) {
      bestDist = t;
      bestLimb = b.limb;
    }
  }

  if (bestLimb === null) return null;
  return { limb: bestLimb, dist: bestDist };
}
